import { UIKit } from "@/shared/ui";
import { EDITOR_OVERLAY_COMPOSITING_STYLE } from "@/shared/config/layout";
/**
 * Виджет «Панель инструментов» — верх редактора.
 *
 * Две строки, как в профессиональных планировщиках:
 *   верхняя  — проект: сохранение, экспорт, режим просмотра 2D / 3D;
 *   нижняя   — работа с чертежом: инструменты, история, отображение.
 * Так действия над проектом не путаются с инструментами черчения.
 */
import { useEffect, useEffectEvent, useState, type ReactNode } from "react";
import { toast } from "react-toastify";
import { Tool, ViewMode, useEditorStore } from "@/widgets/editor";
import { useAuthStore, useProjectStore } from "@/shared/store";
import { usePlanHistory } from "@/shared/hooks";
import { HotkeysHelp } from "@/widgets/hotkeys-help";
import { ExportDialog } from "@/widgets/plan-export";
import { EditorResetPlanDialog } from "@/widgets/tool-panel";
import { useUnderlayRecognitionStore } from "@/widgets/underlay";
import { ToolbarMobileModal } from "./ToolbarMobileModal";

const VIEW_OPTIONS: { value: ViewMode; label: string; icon: ReactNode; title: string }[] = [
	{ value: ViewMode.TwoD, label: "2D", icon: <UIKit.IconView2D />, title: "Чертёж" },
	{
		value: ViewMode.Elevation,
		label: "Развёртки",
		icon: <UIKit.IconElevation />,
		title: "Развёртки стен по помещениям",
	},
	{ value: ViewMode.ThreeD, label: "3D", icon: <UIKit.IconView3D />, title: "Трёхмерный вид" },
];

export const EditorToolbar = ({
	projectId,
	onSelectProject,
}: {
	projectId: string | null;
	onSelectProject: () => void;
}) => {
	const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
	const viewMode = useEditorStore((s) => s.viewMode);
	const setViewMode = useEditorStore((s) => s.setViewMode);
	const tool = useEditorStore((s) => s.tool);
	const setTool = useEditorStore((s) => s.setTool);
	const setSelection = useEditorStore((s) => s.setSelection);
	const setOverlayTopOffset = useEditorStore((s) => s.setOverlayTopOffset);
	const isReadOnly = useEditorStore((s) => s.isReadOnly);
	const demo = useProjectStore((s) => s.demo);
	const resetPlan = useProjectStore((s) => s.resetPlan);
	const [confirmingReset, setConfirmingReset] = useState(false);
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const { undo, redo, canUndo, canRedo } = usePlanHistory();
	const saveProject = useEditorStore((s) => s.saveProject);

	/** Серверный проект уходит на сервер сразу, остальное — в браузер; итог — уведомлением */
	const handleSave = async () => {
		if (saveProject === null) {
			toast.info("Создайте проект, чтобы сохранять изменения");
			return;
		}
		try {
			const result = await saveProject();
			if (result === "synced") toast.success("Проект сохранён");
			else toast.info("Проект сохранён в браузере — отправим на сервер, когда появится связь");
		} catch (error: unknown) {
			console.error("Сохранение проекта не удалось", error);
			toast.error("Не удалось сохранить проект");
		}
	};

	const onSaveHotkey = useEffectEvent(() => {
		if (!isAuthenticated || isReadOnly || demo || projectId === null) return;
		void handleSave();
	});

	// Ctrl/Cmd+S — привычное сохранение вместо диалога браузера
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== "s") return;
			e.preventDefault();
			onSaveHotkey();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, []);

	const drawingDisabled = viewMode !== ViewMode.TwoD;
	const mobileMenuActive = tool === Tool.Comment || tool === Tool.Erase;
	const toggleComment = () => setTool(tool === Tool.Comment ? Tool.Select : Tool.Comment);
	const toggleErase = () => setTool(tool === Tool.Erase ? Tool.Select : Tool.Erase);
	// Смета — отдельной кнопкой, а не вкладкой рядом с видами чертежа; повторное нажатие
	// возвращает к плану
	const estimateOpen = viewMode === ViewMode.Estimate;
	const toggleEstimate = () => setViewMode(estimateOpen ? ViewMode.TwoD : ViewMode.Estimate);

	return (
		<UIKit.TopToolbar
			style={viewMode === ViewMode.TwoD ? EDITOR_OVERLAY_COMPOSITING_STYLE : undefined}
			onOffsetChange={setOverlayTopOffset}
			start={
				<>
					<UIKit.SegmentedControl value={viewMode} options={VIEW_OPTIONS} onChange={setViewMode} />
					<UIKit.VerticalDivider />
					<UIKit.ToolButton
						icon={<UIKit.IconUndo />}
						label="Отменить"
						hotkey="Ctrl+Z"
						disabled={isReadOnly || !canUndo}
						onClick={undo}
					/>
					<UIKit.ToolButton
						icon={<UIKit.IconRedo />}
						label="Повторить"
						hotkey="Ctrl+Shift+Z"
						disabled={isReadOnly || !canRedo}
						onClick={redo}
					/>
					<UIKit.ToolButton
						icon={<UIKit.IconSave />}
						label="Сохранить"
						hotkey="Ctrl+S"
						// Демо не сохраняется — свой проект вернётся при выходе из демо
						disabled={isReadOnly || !isAuthenticated || demo || projectId === null}
						onClick={() => void handleSave()}
					/>
					<UIKit.VerticalDivider />
					<UIKit.ToolButton
						icon={<UIKit.IconComment />}
						label="Комментарий"
						active={tool === Tool.Comment}
						disabled={isReadOnly || drawingDisabled}
						onClick={toggleComment}
					/>
					<UIKit.ToolButton
						icon={<UIKit.IconTrash />}
						label="Удалить"
						active={tool === Tool.Erase}
						disabled={isReadOnly || drawingDisabled}
						onClick={toggleErase}
					/>
					<ExportDialog />
					<UIKit.VerticalDivider />
					<UIKit.ToolButton
						icon={<UIKit.IconReset />}
						label="Сбросить чертёж"
						disabled={isReadOnly || drawingDisabled}
						onClick={() => setConfirmingReset(true)}
					/>
				</>
			}
			end={
				<>
					{/* Смета — не инструмент, а результат чертежа: стоит отдельно справа, с подписью.
						    Суммы на кнопке нет — при примерных ценах она вводила бы в заблуждение */}
					<UIKit.Button
						type="button"
						aria-pressed={estimateOpen}
						title={estimateOpen ? "Вернуться к чертежу" : "Открыть смету работ и материалов"}
						className={`flex h-8 items-center rounded-lg p-0 text-sm transition-colors ${
							estimateOpen
								? "border-accent bg-accent text-overlay-ink hover:border-accent-hover hover:bg-accent-hover"
								: "border-border bg-chrome text-ink hover:border-accent hover:text-accent"
						}`}
						onClick={toggleEstimate}
					>
						<UIKit.Text as="span" className="flex items-center gap-1.5 px-2.5 font-medium">
							<UIKit.IconEstimate />
							Смета
						</UIKit.Text>
					</UIKit.Button>
					<UIKit.VerticalDivider />
					<UIKit.ToolButton
						icon={<UIKit.IconDemo />}
						label="Выбор проекта"
						onClick={onSelectProject}
					/>
					<HotkeysHelp />
				</>
			}
			mobile={
				<>
					<UIKit.SegmentedControl value={viewMode} options={VIEW_OPTIONS} onChange={setViewMode} />
					<div className="ml-auto flex items-center gap-1">
						<UIKit.Button
							type="button"
							aria-pressed={estimateOpen}
							title={estimateOpen ? "Вернуться к чертежу" : "Открыть смету работ и материалов"}
							className={`flex h-7 items-center px-2 py-0 text-xs font-medium ${
								estimateOpen
									? "border-accent bg-accent text-overlay-ink"
									: "border-border bg-chrome text-ink hover:border-accent hover:text-accent"
							}`}
							onClick={toggleEstimate}
						>
							Смета
						</UIKit.Button>
						<UIKit.ToolButton
							icon={<UIKit.IconDemo />}
							label="Выбор проекта"
							onClick={onSelectProject}
						/>
						<UIKit.ToolButton
							icon={<UIKit.IconMore />}
							label="Действия"
							active={mobileMenuActive}
							onClick={() => setMobileMenuOpen(true)}
						/>
					</div>
				</>
			}
		>
			<ToolbarMobileModal
				open={mobileMenuOpen}
				onClose={() => setMobileMenuOpen(false)}
				drawingDisabled={isReadOnly || drawingDisabled}
				commentActive={tool === Tool.Comment}
				eraseActive={tool === Tool.Erase}
				onToggleComment={toggleComment}
				onToggleErase={toggleErase}
				estimateActive={estimateOpen}
				onToggleEstimate={toggleEstimate}
				onReset={() => setConfirmingReset(true)}
			/>
			{confirmingReset && (
				<EditorResetPlanDialog
					onClose={() => setConfirmingReset(false)}
					onConfirm={() => {
						setConfirmingReset(false);
						useUnderlayRecognitionStore.getState().clear();
						resetPlan();
						setSelection(null);
						setTool(Tool.Select);
					}}
				/>
			)}
		</UIKit.TopToolbar>
	);
};
